import React, { useCallback } from "react";
import axios from "axios";
import { isTokenExpired, useAuth } from "../contexts/AuthContext";
import { isWithinIdleWindow } from "../contexts/sessionActivity";
import constant from "@/config/constant";

const statusCode = [401, 403, 422, 400, 500, 404, 409, 406, 400];

interface ApiResponse {
  status: number;
  message?: string;
  data?: any;
}

axios.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    if (
      error &&
      error.response &&
      statusCode.includes(error?.response?.status)
    ) {
      return Promise.resolve(error.response);
    } else {
      return Promise.reject(error);
    }
  }
);

const getHeaders = (
  isAuthorized: boolean,
  token: string,
  userDetail: string,
  deviceId: string,
  appSource: string
) => {
  const config: {
    headers: { [key: string]: string };
  } = {
    headers: { Accept: "application/json" },
  };
  if (isAuthorized) config.headers["Authorization"] = `Bearer ${token}`;
  config.headers["app-source"] = appSource;
  config.headers["userdetail"] = userDetail;
  config.headers["deviceid"] = deviceId;
  return config.headers;
};

// const isTokenExpired = () => {
//   const expirationTime = localStorage.getItem("tokenExpiration");
//   if (!expirationTime) return true;
//   return Number(expirationTime) <= new Date().getTime();
// };

const useRequestToServer = () => {
  const { logout, refreshSession, userDetail, user } = useAuth();

  const requestToServer = useCallback(
    async (
      url: string,
      data: unknown,
      method: string,
      isAuthorized = true,
      responseType: "json" | "blob" = "json",
      appSource = constant.appSources.leadership
    ): Promise<ApiResponse> => {
      const requestUrl = `${import.meta.env.VITE_API_URL}${url}` || "";
      const isProtected = isAuthorized && !url.includes("/login");

      let token = localStorage.getItem("token") || "";

      // Token already expired before we even send: silently refresh if the user
      // is still active, otherwise end the session. Only an idle user (past the
      // idle cap) gets logged out here — an active user is renewed transparently.
      if (isProtected && isTokenExpired(token)) {
        const refreshed = isWithinIdleWindow() ? await refreshSession() : false;
        if (refreshed) {
          token = localStorage.getItem("token") || "";
        } else {
          logout();
          throw new Error("Session expired. Please login again.");
        }
      }

      const deviceId = localStorage.getItem("deviceId") || "";
      const buildHeaders = (authToken: string) => {
        const headers = getHeaders(
          isAuthorized,
          authToken,
          userDetail,
          deviceId,
          appSource
        );
        if (url.includes("/login")) headers["request-type"] = "login";
        if (url.includes("/callback")) headers["request-type"] = "callback";
        return headers;
      };

      const send = async (authToken: string) => {
        try {
          return await axios({
            method,
            url: requestUrl,
            headers: buildHeaders(authToken),
            data,
            responseType,
          });
        } catch (error) {
          if (!navigator.onLine) throw new Error("OfflineError");
          throw error;
        }
      };

      // The axios response interceptor resolves 401s instead of throwing, so
      // they surface here as a response with status 401.
      let response = await send(token);

      if (response && response.status === 401 && isProtected) {
        // Backend rejected the token (e.g. clock skew / revoked) despite our
        // local check — attempt one silent refresh + retry while the user is
        // active before giving up.
        const refreshed = isWithinIdleWindow() ? await refreshSession() : false;
        if (refreshed) {
          token = localStorage.getItem("token") || "";
          response = await send(token);
        }
        if (!response || response.status === 401) {
          logout();
          throw new Error("Session expired");
        }
      }

      return response as unknown as ApiResponse;
    },
    [logout, refreshSession, userDetail, user]
  ); // Add dependencies for useCallback

  return { requestToServer };
};

export default useRequestToServer;
